import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DollarSign, MapPin, Clock, Lock } from 'lucide-react';
import { Grant } from '@/hooks/useGrants';

interface GrantAnnouncementProps {
  grant: Grant;
  onSignInRequired: () => void;
}

export const GrantAnnouncement = ({ grant, onSignInRequired }: GrantAnnouncementProps) => {
  const formatDeadline = (dateString: string | null) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const isDeadlineSoon = (dateString: string | null) => {
    if (!dateString) return false;
    try {
      const deadline = new Date(dateString);
      const today = new Date();
      const daysUntil = Math.ceil((deadline.getTime() - today.getTime()) / (1000 * 3600 * 24));
      return daysUntil <= 30 && daysUntil >= 0;
    } catch {
      return false;
    }
  };

  return (
    <Card className="bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <DollarSign className="w-5 h-5 text-primary" />
          </div>
          
          <div className="flex-1 space-y-2">
            <div className="flex items-start justify-between">
              <h4 className="font-semibold text-foreground line-clamp-2">
                🎯 Grant Opportunity: {grant.funder_program}
              </h4>
              {grant.deadline_date && (
                <Badge 
                  variant={isDeadlineSoon(grant.deadline_date) ? "destructive" : "secondary"}
                  className="ml-2 flex-shrink-0"
                >
                  <Clock className="w-3 h-3 mr-1" />
                  {formatDeadline(grant.deadline_date)}
                </Badge>
              )}
            </div>
            
            {grant.program_focus && (
              <p className="text-sm text-muted-foreground line-clamp-2">
                {grant.program_focus}
              </p>
            )}
            
            <div className="flex flex-wrap gap-2">
              {grant.geography && (
                <Badge variant="outline" className="text-xs">
                  <MapPin className="w-3 h-3 mr-1" />
                  {grant.geography}
                </Badge>
              )}
              
              {grant.typical_grant_size && (
                <Badge variant="outline" className="text-xs">
                  <DollarSign className="w-3 h-3 mr-1" />
                  {grant.typical_grant_size}
                </Badge>
              )}

              {grant.eligibility_type && (
                <Badge variant="outline" className="text-xs">
                  {grant.eligibility_type}
                </Badge>
              )}
            </div>

            <div className="bg-muted/50 rounded-lg p-3 border border-dashed border-muted-foreground/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Lock className="w-4 h-4" />
                  <span>Full details, contact info & application links available</span>
                </div>
                <Button 
                  size="sm" 
                  onClick={onSignInRequired}
                  className="flex-shrink-0"
                >
                  Sign In to View
                </Button>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};